/**
 * Metaobject-driven mega menu — lazy panel fetch + brand-rail switching.
 *
 * The desktop nav (snippets/header-full-menu.liquid) renders only a lightweight
 * placeholder per top-level link:
 *   li.menu-item-has-megamenu
 *     .mega-menu-mo-placeholder[data-mega-handle][data-mega-url]
 *
 * On hover/focus of the top-level <li>, we fetch the real panel from the
 * `mega-menu-panel` section (rendered in the context of the matching collection
 * URL, so the heavy markup + collection.products queries stay OUT of the initial
 * page document). The fetched HTML is injected once and cached.
 *
 * After injection, the panel behaves as before:
 *   .mega-menu-mo
 *     .mega-menu-mo__rail   -> a[data-mo-target="N"]
 *     .mega-menu-mo__panels -> [data-mo-panel="N"]
 * Hover/keyboard-focus a rail link to activate the matching panel.
 *
 * The mobile drawer (snippets/header-mobile-menu.liquid) is lazy too: it renders
 * one li.mobile-menu-mo-placeholder[data-mega-handle] per mega menu, and the
 * L2/L3 rows of all menus are injected from the `mobile-menu-panels` section
 * once the page has loaded (or as soon as the hamburger is pressed). Those rows
 * are also the crawlable <a href> links to the menu's collections.
 */
(function () {
  var SECTION_ID = 'mega-menu-panel';
  // Cache fetched panel HTML by handle so repeat hovers cost zero requests.
  var panelCache = {};

  function activate(root, index) {
    var rails = root.querySelectorAll('.mega-menu-mo__rail-item');
    var panels = root.querySelectorAll('.mega-menu-mo__panel');

    root.querySelectorAll('.mega-menu-mo__rail-link').forEach(function (link) {
      var match = link.getAttribute('data-mo-target') === index;
      link.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    rails.forEach(function (item) {
      var link = item.querySelector('.mega-menu-mo__rail-link');
      item.classList.toggle('is-active', !!link && link.getAttribute('data-mo-target') === index);
    });
    panels.forEach(function (panel) {
      var match = panel.getAttribute('data-mo-panel') === index;
      panel.classList.toggle('is-active', match);
      if (match) {
        panel.removeAttribute('aria-hidden');
        // Unveil any lazy images now that the panel is shown.
        if (window.lazySizes) {
          panel.querySelectorAll('img.lazyload').forEach(function (img) {
            window.lazySizes.loader.unveil(img);
          });
        }
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function init(root) {
    if (root.dataset.moBound) return;
    root.dataset.moBound = 'true';

    var rail = root.querySelector('.mega-menu-mo__rail');
    if (!rail) return;

    function onActivate(event) {
      var link = event.target.closest('.mega-menu-mo__rail-link');
      if (!link || !rail.contains(link)) return;
      activate(root, link.getAttribute('data-mo-target'));
    }

    rail.addEventListener('mouseover', onActivate);
    rail.addEventListener('focusin', onActivate);

    // Expandable L3 cards: click to reveal the product panel below.
    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('.mega-menu-mo__card-toggle');
      if (!toggle || !root.contains(toggle)) return;
      var panelId = toggle.getAttribute('aria-controls');
      var panel = panelId && root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(panelId) : panelId));
      if (!panel) return;
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (open) {
        panel.setAttribute('hidden', '');
      } else {
        panel.removeAttribute('hidden');
        loadImages(panel);
      }
      // A mouse click leaves the button focused, and the header's
      // :focus-within rule then pins this mega menu open even after the
      // pointer leaves (stacking under other hovered menus). Blur so
      // hover-driven open/close resumes; keyboard activation (detail 0)
      // keeps focus for accessibility.
      if (event.detail > 0) toggle.blur();
    });
  }

  // Fill in image src/srcset for the panel's lazy product images.
  function loadImages(scope) {
    if (scope.dataset.moImgLoaded) return;
    scope.dataset.moImgLoaded = 'true';
    scope.querySelectorAll('img.mega-menu-mo__lazy-img').forEach(function (img) {
      var srcset = img.getAttribute('data-mega-srcset');
      var sizes = img.getAttribute('data-mega-sizes');
      var src = img.getAttribute('data-mega-src');
      if (sizes) img.setAttribute('sizes', sizes);
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
    });
  }

  // Build the section-fetch URL. data-mega-url is the menu link's own URL
  // (already locale-aware, e.g. "/en/collections/sneakers"), so we append the
  // section_id directly without re-prefixing the locale root.
  function panelUrl(rawUrl) {
    var sep = rawUrl.indexOf('?') > -1 ? '&' : '?';
    return rawUrl + sep + 'section_id=' + SECTION_ID;
  }

  // Fetch + inject a panel into its placeholder, then wire it up.
  function loadPanel(li) {
    var placeholder = li.querySelector('.mega-menu-mo-placeholder');
    if (!placeholder || placeholder.dataset.moState) return;
    var handle = placeholder.getAttribute('data-mega-handle');
    var url = placeholder.getAttribute('data-mega-url');
    if (!handle || !url) return;

    placeholder.dataset.moState = 'loading';

    var inject = function (html) {
      // Section Rendering returns the section wrapper; extract its inner panel.
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var panel = doc.querySelector('.mega-menu-mo');
      if (!panel) {
        placeholder.dataset.moState = 'error';
        return;
      }
      var imported = document.importNode(panel, true);
      placeholder.replaceWith(imported);
      placeholder = null;
      init(imported);
      loadImages(imported);
    };

    if (panelCache[handle]) {
      inject(panelCache[handle]);
      return;
    }

    fetch(panelUrl(url), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        panelCache[handle] = html;
        inject(html);
      })
      .catch(function () {
        // Leave the placeholder so the next hover can retry.
        if (placeholder) placeholder.dataset.moState = '';
      });
  }

  function bindLazyPanels() {
    document.querySelectorAll('.menu-item-has-megamenu').forEach(function (li) {
      if (li.dataset.moLazyBound) return;
      if (!li.querySelector('.mega-menu-mo-placeholder')) return;
      li.dataset.moLazyBound = 'true';
      var load = function () { loadPanel(li); };
      li.addEventListener('mouseenter', load);
      li.addEventListener('focusin', load);
    });
  }

  // After load, during idle time, warm the panel cache so the first real hover
  // injects instantly. This runs off the critical path — it does NOT affect the
  // initial document or LCP, only prefetches the section HTML in the background.
  function prefetchPanels() {
    document.querySelectorAll('.mega-menu-mo-placeholder').forEach(function (placeholder) {
      var handle = placeholder.getAttribute('data-mega-handle');
      var url = placeholder.getAttribute('data-mega-url');
      if (!handle || !url || panelCache[handle]) return;
      fetch(panelUrl(url), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) { if (html) panelCache[handle] = html; })
        .catch(function () {});
    });
  }

  // ── Mobile drawer ───────────────────────────────────────────────────────
  var MOBILE_SECTION_ID = 'mobile-menu-panels';
  var mobileHtml = null;
  var mobileLoading = false;

  // Shopify.routes.root is locale-aware ("/" or "/en/"), and so is the markup
  // (translated titles, prefixed URLs) — hence one cache entry per root.
  function mobilePanelsUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + '?section_id=' + MOBILE_SECTION_ID;
  }

  // Swap each placeholder for the rows of the <ul> with the same handle.
  function fillMobilePlaceholders(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var lists = doc.querySelectorAll('ul[data-mega-handle]');
    if (!lists.length) return false;
    document.querySelectorAll('.mobile-menu-mo-placeholder').forEach(function (placeholder) {
      var handle = placeholder.getAttribute('data-mega-handle');
      var rows = document.createDocumentFragment();
      lists.forEach(function (list) {
        if (list.getAttribute('data-mega-handle') !== handle) return;
        Array.prototype.forEach.call(list.children, function (row) {
          rows.appendChild(document.importNode(row, true));
        });
      });
      // An unmatched placeholder is dropped too, rather than shimmering forever.
      placeholder.replaceWith(rows);
    });
    return true;
  }

  function loadMobilePanels() {
    if (!document.querySelector('.mobile-menu-mo-placeholder')) return;
    if (mobileHtml) {
      fillMobilePlaceholders(mobileHtml);
      return;
    }
    if (mobileLoading) return;

    var url = mobilePanelsUrl();
    var cacheKey = 'ns-mobile-menu:' + url;
    var designMode = window.Shopify && Shopify.designMode;
    if (!designMode) {
      try {
        var cached = sessionStorage.getItem(cacheKey);
        if (cached && fillMobilePlaceholders(cached)) {
          mobileHtml = cached;
          return;
        }
      } catch (e) {}
    }

    mobileLoading = true;
    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        mobileLoading = false;
        if (!fillMobilePlaceholders(html)) return;
        mobileHtml = html;
        try { sessionStorage.setItem(cacheKey, html); } catch (e) {}
      })
      .catch(function () {
        // Placeholders stay; the next hamburger press retries.
        mobileLoading = false;
      });
  }

  function boot() {
    // Pre-rendered panels still get wired up.
    document.querySelectorAll('.mega-menu-mo').forEach(init);
    bindLazyPanels();
    var schedule = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1200); };
    schedule(prefetchPanels);

    // Mobile drawer rows: after load + idle, so neither the fetch nor the
    // ~1,500-node injection competes with LCP. Pressing the hamburger first
    // loads them right away.
    var toggle = document.querySelector('.mobile-toggle');
    if (toggle) {
      toggle.addEventListener('pointerdown', loadMobilePanels, { passive: true });
      toggle.addEventListener('keydown', loadMobilePanels);
    }
    if (document.readyState === 'complete') {
      schedule(loadMobilePanels);
    } else {
      window.addEventListener('load', function () { schedule(loadMobilePanels); }, { once: true });
    }
    // Theme editor: a re-rendered header comes back with empty placeholders.
    document.addEventListener('shopify:section:load', loadMobilePanels);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
